import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { Loader, EmptyState } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Progress } from "../components/ui/progress";
import { Target, Pencil } from "lucide-react";

const PILLARS = ["Financial Sustainability", "Program Impact", "Growth", "Governance", "Capacity Building"];
const G_STATUS = ["On Track", "At Risk", "Behind", "Achieved"];
const sTone = (s) => ({ "On Track": "green", "Achieved": "green", "At Risk": "amber", "Behind": "red" }[s] || "gray");

export default function Strategy() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer", "board"].includes(user?.role);
  const [goals, setGoals] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ goal: "", pillar: "Financial Sustainability", kpi_metric: "", target: "", current: "", unit: "", status: "On Track" });

  const load = () => api.get("/strategic-goals").then((r) => setGoals(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/strategic-goals", { ...form, target: Number(form.target || 0), current: Number(form.current || 0) }); toast.success("Goal added"); setOpen(false); setForm({ goal: "", pillar: "Financial Sustainability", kpi_metric: "", target: "", current: "", unit: "", status: "On Track" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };

  const saveEdit = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.patch(`/strategic-goals/${edit.id}`, { current: Number(edit.current || 0), status: edit.status }); toast.success("Progress updated"); setEdit(null); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };

  if (!goals) return <Loader />;
  const fmtVal = (v, unit) => unit === "$" ? `$${fmtNum(v)}` : `${fmtNum(v)}${unit ? " " + unit : ""}`;

  return (
    <div>
      <PageHeader title="Strategic Planning & KPIs" subtitle="Track progress against organizational goals and key performance indicators."
        action={canManage && <AddButton onClick={() => setOpen(true)} label="Add Goal" testid="add-goal-btn" />} />

      {goals.length === 0 ? <EmptyState title="No strategic goals yet" hint="Add your first goal." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="goals-grid">
          {goals.map((g) => (
            <div key={g.id} className="bg-white border border-stone-200 rounded-xl p-6" data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg bg-forest/10 flex items-center justify-center"><Target className="h-4 w-4 text-forest" /></span>
                  <StatusBadge tone="blue">{g.pillar}</StatusBadge>
                </div>
                <StatusBadge tone={sTone(g.status)}>{g.status}</StatusBadge>
              </div>
              <h3 className="font-heading text-lg mt-3 text-[#1A1A1A]">{g.goal}</h3>
              <div className="text-xs text-[#5C5C54] mt-1">{g.kpi_metric}</div>
              <div className="mt-4 flex items-end justify-between">
                <span className="font-mono text-xl font-semibold">{fmtVal(g.current, g.unit)}</span>
                <span className="text-sm text-[#5C5C54]">of {fmtVal(g.target, g.unit)}</span>
              </div>
              <Progress value={Math.min(g.progress, 100)} className="h-2 mt-2" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[#5C5C54]">{g.progress}% complete</span>
                {canManage && <button onClick={() => setEdit({ ...g })} data-testid={`edit-goal-${g.id}`} className="text-xs text-forest font-medium hover:underline flex items-center gap-1"><Pencil className="h-3 w-3" /> Update</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Add Strategic Goal" onSubmit={add} busy={busy} testid="goal-modal">
        <Field label="Goal" span={2}><TextInput required value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} /></Field>
        <Field label="Pillar"><SelectInput value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })} options={PILLARS} /></Field>
        <Field label="KPI Metric"><TextInput value={form.kpi_metric} onChange={(e) => setForm({ ...form, kpi_metric: e.target.value })} /></Field>
        <Field label="Target"><TextInput type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></Field>
        <Field label="Current"><TextInput type="number" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} /></Field>
        <Field label="Unit ($, families, months…)" span={2}><TextInput value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></Field>
      </Modal>

      <Modal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Update Progress" onSubmit={saveEdit} busy={busy} testid="goal-edit-modal">
        {edit && <>
          <Field label="Current Value"><TextInput type="number" value={edit.current} onChange={(e) => setEdit({ ...edit, current: e.target.value })} /></Field>
          <Field label="Status"><SelectInput value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} options={G_STATUS} /></Field>
        </>}
      </Modal>
    </div>
  );
}
